import {produce} from 'immer';

type ActionLike = {type: string};
type DispatchLike<A extends ActionLike = ActionLike> = (action: A) => void;

type FormKeys<T> = string & keyof T;

export type AnyValues = {
    [key: string]: unknown;
};

export type FieldsIssues<T = AnyValues> = {
    [K in keyof T]?: string | undefined;
};

type LegacySubmitSuccessSentinel = true;
export type SubmitResultSentinel =
    | ('SUBMIT_SETTLED' & {
          [k in {readonly SubmitSuccessSentinel: unique symbol}['SubmitSuccessSentinel']]: never;
      })
    | LegacySubmitSuccessSentinel
    | void
    | undefined;
export const SubmitGotResult: SubmitResultSentinel = 'SUBMIT_SETTLED' as any;

type LegacySubmitDefectSentinel = false;
export type SubmitDefectSentinel =
    | ('SUBMIT_DEFECT' & {
          [k in {readonly SubmitDefectSentinel: unique symbol}['SubmitDefectSentinel']]: never;
      })
    | LegacySubmitDefectSentinel;
export const SubmitGotDefect: SubmitDefectSentinel = 'SUBMIT_DEFECT' as any;

export type SubmitHandler<V extends AnyValues> = () => MaybePromise<
    FieldsIssues<V> | SubmitResultSentinel | SubmitDefectSentinel
>;

// ================================================================================
// # FieldState
//
// Standalone field definition. Unlikely to be needed, prefer forms
// even if your form has just one filed.
// ================================================================================

export interface FieldState<V> {
    value: V;
    completed: boolean;
    wasModified: number;
    wasBlurred: number;
    wasFocused: number;
}

export namespace FieldState {
    export function from<V>(initialValue: V): FieldState<V> {
        return {
            value: initialValue,
            completed: false,
            wasModified: 0,
            wasBlurred: 0,
            wasFocused: 0,
        };
    }
}

// ================================================================================
// # FormFieldsState
//
// State of just the fields, meant to be used when
// submit state is managed elsewhere.
// ================================================================================

export interface FormFieldsState<V extends AnyValues> {
    fields: FormFieldsState.FieldsRecord<V>;
    initialValues: V;
}

export namespace FormFieldsState {
    export type FieldsRecord<V extends AnyValues> = {
        [K in keyof V]: FieldState<V[K]>;
    };

    export function from<V extends AnyValues>(initialValues: V): FormFieldsState<V> {
        const state: FormFieldsState<V> = {
            fields: {} as FieldsRecord<V>,
            initialValues,
        };
        for (const key of getFields(state)) {
            state.fields[key] = FieldState.from(initialValues[key]);
        }
        return state;
    }

    export function toValues<V extends AnyValues>(state: FormFieldsState<V>): V {
        const values = {} as V;
        for (const key in state.fields) {
            values[key] = state.fields[key].value;
        }
        return values;
    }

    export function withRecomputedValues<F extends FormFieldsState<any>>(
        state: F,
        recompute: (values: FormState.inferValues<F>) => FormState.inferValues<F>
    ) {
        return produce(state, (draft: F) => {
            const recomputedValues = recompute(toValues(draft));
            for (const key in state.fields) {
                draft.fields[key].value = recomputedValues[key];
            }
        });
    }

    /**
     * Alternative implementations that were considered:
     *
     * ### 1
     * ```ts
     *  // fieldNames created upon construction, unchanged though out state lifecycle
     * return state.fieldNames
     * ```
     * ### 2
     * ```ts
     * // problematic, additional unknown fields could be added in spread operations
     * return Object.keys(state.fields)
     * ```
     * ### 3 (current)
     * ```ts
     * return Object.keys(state.initialValues);
     * ```
     */
    export function getFields<V extends AnyValues>(state: FormFieldsState<V>): FormKeys<V>[] {
        return Object.keys(state.initialValues);
    }

    export type Action<V extends AnyValues> =
        | FormAction.FieldBlurred<V>
        | FormAction.FieldChange<V>
        | FormAction.FieldsSetAutomatically<V>
        | FormAction.SubmitInit
        | FormAction.SubmitAttempt;

    export function reduce<V extends AnyValues>(
        formState: FormFieldsState<V>,
        action: Action<V> | ActionLike
    ): FormFieldsState<V> {
        if (FormAction.SubmitAttempt.match(action) || FormAction.SubmitInit.match(action)) {
            return produce(formState, recipeForFieldsCompletion());
        }
        if (FormAction.FieldsSetAutomatically.match(action)) {
            return produce(formState, recipeForFieldsAutoSet(action));
        }
        if (FormAction.FieldChange.match(action)) {
            return produce(formState, recipeForFieldChange(action));
        }
        if (FormAction.FieldBlurred.match(action)) {
            return produce(formState, recipeForFieldBlur(action));
        }
        if (FormAction.FieldFocused.match(action)) {
            return produce(formState, recipeForFieldFocus(action));
        }
        if (FormAction.Reset.match(action)) {
            return from(formState.initialValues);
        }
        return formState;
    }

    function recipeForFieldsCompletion<V extends AnyValues>(fields?: (keyof V)[]) {
        return (formState: FormState<V>): void => {
            fields ||= FormFieldsState.getFields(formState);
            for (const key of fields) {
                formState.fields[key].completed = true;
            }
        };
    }

    function recipeForFieldsAutoSet<V extends AnyValues>(action: OmitType<FormAction.FieldsSetAutomatically<V>>) {
        const {entries, ts} = action;
        return (formState: FormState<V>): void => {
            for (const entry of entries) {
                if (!entry) {
                    continue;
                }
                const [fieldName, fieldValue] = entry;
                recipeForFieldChange({ts, fieldName, fieldValue})(formState);
                recipeForFieldBlur({ts, fieldName})(formState);
            }
        };
    }

    function recipeForFieldChange<V extends AnyValues, K extends keyof V>(
        action: OmitType<FormAction.FieldChange<V, K>>
    ) {
        const {fieldName, fieldValue, ts} = action;
        return (formState: FormState<V>): void => {
            const field = formState.fields[fieldName];
            if (field.value !== fieldValue) {
                field.value = fieldValue;
                field.wasModified = ts;
            }
        };
    }

    function recipeForFieldBlur<V extends AnyValues>(action: OmitType<FormAction.FieldBlurred<V>>) {
        const {fieldName, ts} = action;
        return (formState: FormState<V>): void => {
            const field = formState.fields[fieldName];
            field.wasBlurred = ts;
            if (field.wasModified) {
                field.completed = true;
            }
        };
    }

    function recipeForFieldFocus<V extends AnyValues>(action: OmitType<FormAction.FieldFocused<V>>) {
        const {fieldName, ts} = action;
        return (formState: FormState<V>): void => {
            const field = formState.fields[fieldName];
            field.wasFocused = ts;
        };
    }
}

// ========================================
// Fields actions

export namespace FormAction {
    export function FieldChange<V extends AnyValues, K extends keyof V>(fieldName: K, value: ValueOrEvent<V[K]>) {
        if (ValueOrEvent.isEvent(value)) {
            value = value.currentTarget.value;
        }
        return {
            type: FieldChange.type,
            fieldName,
            fieldValue: value,
            ts: Date.now(),
        };
    }
    FieldChange.type = 'FormState:fieldChanged' as const;
    FieldChange.match = <V extends AnyValues, K extends keyof V>(
        action: ActionLike | FieldChange<V, K>
    ): action is FieldChange<V, K> => action.type === FieldChange.type;

    export type FieldChange<V extends AnyValues, K extends keyof V = keyof V> = ReturnType<typeof FieldChange<V, K>>;

    export function FieldBlurred<V extends AnyValues>(fieldName: keyof V) {
        return {
            type: FieldBlurred.type,
            fieldName,
            ts: Date.now(),
        };
    }
    FieldBlurred.type = 'FormState:fieldBlurred' as const;
    FieldBlurred.match = createMatcherFor(FieldBlurred<AnyValues>);
    export type FieldBlurred<V extends AnyValues> = ReturnType<typeof FieldBlurred<V>>;

    export function FieldFocused<V extends AnyValues>(fieldName: keyof V) {
        return {
            type: FieldFocused.type,
            fieldName,
            ts: Date.now(),
        };
    }

    FieldFocused.type = 'FormState:fieldFocused' as const;
    FieldFocused.match = createMatcherFor(FieldBlurred<AnyValues>);
    export type FieldFocused<V extends AnyValues> = ReturnType<typeof FieldFocused<V>>;

    type Entries<V extends AnyValues> = readonly {
        [K in keyof V & string]: readonly [K, V[K]] | Falsy;
    }[keyof V & string][];

    export function FieldsSetAutomatically<V extends AnyValues>(entries: NoInfer<Entries<V>>) {
        return {
            type: FieldsSetAutomatically.type,
            entries,
            ts: Date.now(),
        };
    }
    FieldsSetAutomatically.type = 'FormState:fieldsAutoSet';
    FieldsSetAutomatically.match = <V extends AnyValues>(
        action: ActionLike | FieldsSetAutomatically<V>
    ): action is FieldsSetAutomatically<V> => action.type === FieldsSetAutomatically.type;

    export type FieldsSetAutomatically<V extends AnyValues> = ReturnType<typeof FieldsSetAutomatically<V>>;

    export function bindFieldsActions<V extends AnyValues>(dispatch: DispatchLike) {
        return {
            fieldChanged: <K extends keyof V>(
                fieldName: K,
                value: ValueOrEvent<V[K]>
                //
            ) => dispatch(FieldChange(fieldName, value)),
            fieldBlurred: (
                fieldName: keyof V
                //
            ) => dispatch(FieldBlurred(fieldName)),
            fieldsSetAutomatically: (
                entries: Entries<V>
                //
            ) => dispatch(FieldsSetAutomatically(entries)),
        };
    }
}

// ================================================================================
// # FormSubmitState
//
// Just the state of form submit for cases when it should be managed
// separately from fields.
// ================================================================================

export interface FormSubmitState<V extends AnyValues> {
    submitIssues: FieldsIssues<V>;
    submitInitAt: number;
    submitDoneAt: number;
}

export namespace FormSubmitState {
    export function init<V extends AnyValues>(): FormSubmitState<V> {
        return {
            submitIssues: {},
            submitInitAt: 0,
            submitDoneAt: 0,
        };
    }

    export type Action<V extends AnyValues> =
        | FormAction.SubmitInit
        | FormAction.SubmitDone<V>
        | FormAction.SubmitAttempt;

    export function reduce<V extends AnyValues>(
        state: FormSubmitState<V>,
        action: Action<V> | ActionLike
    ): FormSubmitState<V> {
        if (FormAction.SubmitInit.match(action)) {
            return {
                ...state,
                submitInitAt: action.ts,
            };
        }
        if (FormAction.SubmitDone.match(action)) {
            return {
                ...state,
                submitDoneAt: action.ts,
                submitIssues: action.submitIssues,
            };
        }
        if (FormAction.SubmitFail.match(action)) {
            return {
                ...state,
                submitDoneAt: action.ts,
            };
        }
        if (FormAction.Reset.match(action)) {
            return init();
        }
        return state;
    }

    export function isSubmitting(formState: FormSubmitState<any>) {
        return formState.submitInitAt > formState.submitDoneAt;
    }

    export function selectSubmitProps<V extends AnyValues>(
        formState: FormSubmitState<V>,
        {
            dispatch,
            submit,
        }: {
            dispatch: DispatchLike;
            submit: SubmitHandler<V>;
        }
    ) {
        const isSubmitting = FormSubmitState.isSubmitting(formState);

        async function onSubmit(ev?: EventLike) {
            ev?.preventDefault();

            if (isSubmitting) {
                return;
            }
            try {
                const {isImmediate, value} = await MaybePromise.classify(submit());
                if (isImmediate) {
                    return value;
                }
                dispatch(FormAction.SubmitInit());
                const result = await value;
                if (result === false || result === SubmitGotDefect) {
                    dispatch(FormAction.SubmitFail());
                } else {
                    dispatch(FormAction.SubmitDone(typeof result === 'object' ? result : {}));
                }
            } catch (error) {
                dispatch(FormAction.SubmitFail());
                throw error;
            }
        }

        return {
            isSubmitting,
            onSubmit,
        };
    }
}

// ========================================
// Submit actions

export namespace FormAction {
    export type SubmitAttempt = ReturnType<typeof SubmitAttempt>;

    export function SubmitAttempt() {
        return {
            type: SubmitAttempt.type,
            ts: Date.now(),
        };
    }

    SubmitAttempt.type = 'State:submitAttempt' as const;
    SubmitAttempt.match = createMatcherFor(SubmitAttempt);

    export type SubmitInit = ReturnType<typeof SubmitInit>;

    export function SubmitInit() {
        return {
            type: SubmitInit.type,
            ts: Date.now(),
        };
    }

    SubmitInit.type = 'State:submitInit' as const;
    SubmitInit.match = createMatcherFor(SubmitInit);

    export type SubmitDone<V extends AnyValues> = ReturnType<typeof SubmitDone<V>>;

    export function SubmitDone<V extends AnyValues>(submitIssues: FieldsIssues<V>) {
        return {
            type: SubmitDone.type,
            submitIssues,
            ts: Date.now(),
        };
    }

    SubmitDone.type = 'State:submitDone' as const;
    SubmitDone.match = createMatcherFor(SubmitDone);

    export type SubmitFail = ReturnType<typeof SubmitFail>;

    export function SubmitFail() {
        return {
            type: SubmitFail.type,
            ts: Date.now(),
        };
    }

    SubmitFail.type = 'State:submitFail' as const;
    SubmitFail.match = createMatcherFor(SubmitFail);

    export function bindSubmitActions<V extends AnyValues>(dispatch: DispatchLike) {
        return {
            submitAttempt: () => dispatch(SubmitAttempt()),
            submitInit: () => dispatch(SubmitInit()),
            submitDone: (submitIssues: FieldsIssues<V>) => dispatch(SubmitDone(submitIssues)),
            submitFail: () => dispatch(SubmitFail()),
        };
    }
}

// ================================================================================
// # FormState
//
// The combined, and likely to be most used model.
// ================================================================================

export interface FormState<V extends AnyValues> extends FormFieldsState<V>, FormSubmitState<V> {}

export interface FormStateWithIssues<V extends AnyValues> extends FormState<V> {
    issues: FieldsIssues<V>;
}

export namespace FormStateWithIssues {
    export function identity<V extends AnyValues>(self: FormStateWithIssues<V>) {
        return self;
    }

    export function empty<V extends AnyValues>(base: FormState<V>) {
        return {...base, issues: {}};
    }

    export function fromServerIssues<V extends AnyValues>(base: FormState<V>, issues: FieldsIssues<V>) {
        return {...base, issues};
    }

    export function fromRecipe<V extends AnyValues>(
        base: FormState<V>,
        writeIssues: (issues: FieldsIssues<V>) => void
    ): FormStateWithIssues<V> {
        const state: FormStateWithIssues<V> = {
            ...base,
            issues: {},
        };
        writeIssues(state.issues);
        return state;
    }

    export function fromRules<V extends AnyValues>(
        base: FormState<V>,
        rulesObject: {[K in keyof V]?: Array<(value: V[K], values: V) => string | Falsy>}
    ): FormStateWithIssues<V> {
        const state: FormStateWithIssues<V> = {...base, issues: {}};
        const values = FormFieldsState.toValues(base);
        overFields: for (const key in rulesObject) {
            const fieldRules = rulesObject[key];
            if (!fieldRules) {
                continue;
            }
            const field = base.fields[key];
            overRules: for (const rule of fieldRules) {
                const issue = rule(field.value, values);
                if (!issue) {
                    continue overRules;
                }
                state.issues[key] = issue;
                continue overFields;
            }
        }
        return state;
    }
}

export namespace FormState {
    export const toValues = FormFieldsState.toValues;
    export const withRecomputedValues = FormFieldsState.withRecomputedValues;
    export const getFields = FormFieldsState.getFields;

    export type inferValues<T> = T extends FormState<infer V> ? V : never;

    export function from<V extends AnyValues>(initialValues: V): FormState<V> {
        return {
            ...FormFieldsState.from(initialValues),
            ...FormSubmitState.init(),
        };
    }

    export type InputProps<V extends AnyValues, K extends FormKeys<V>> = ReturnType<typeof selectAnInputProps<V, K>>;
    export function selectAnInputProps<V extends AnyValues, K extends FormKeys<V>>(
        dispatch: DispatchLike,
        state: FormStateWithIssues<V>,
        fieldName: K & string
    ) {
        const field = state.fields[fieldName];
        const result: {
            value: V[K];
            issue?: string;
            isInvalid?: boolean;
            onBlur: () => void;
            onFocus: () => void;
            onChange: (value: ValueOrEvent<V[K]>) => void;
        } = {
            value: field.value,
            onBlur: () => dispatch(FormAction.FieldBlurred(fieldName)),
            onFocus: () => dispatch(FormAction.FieldFocused(fieldName)),
            onChange: value => dispatch(FormAction.FieldChange(fieldName, value)),
        };
        const isModifiedSinceSubmit = field.wasModified >= state.submitDoneAt;

        const relevantSubmitIssue = isModifiedSinceSubmit ? undefined : state.submitIssues[fieldName];
        const relevantFieldIssue = field.completed ? state.issues[fieldName] : undefined;

        const visibleIssue = relevantFieldIssue || relevantSubmitIssue;
        if (visibleIssue) {
            result.issue = visibleIssue;
            result.isInvalid = true;
        }
        return result;
    }

    interface FormViewParameters<V extends AnyValues> extends SubmitPropsParameters<V> {
        //
    }

    export function selectFormView<V extends AnyValues>(state: FormStateWithIssues<V>, params: FormViewParameters<V>) {
        return {
            ...selectSubmitProps(state, params),
            fields: selectInputProps(state, params),
        };
    }

    interface InputPropsParameters {
        dispatch: DispatchLike;
    }

    export type InputPropsRecord<V extends AnyValues> = {
        [K in keyof V]: ReturnType<typeof FormState.selectAnInputProps<V, K & string>>;
    };

    export function selectInputProps<V extends AnyValues>(
        state: FormStateWithIssues<V>,
        {dispatch}: InputPropsParameters
    ) {
        const props = {} as InputPropsRecord<V>;
        const fieldNames = FormFieldsState.getFields(state);
        for (const fieldName of fieldNames) {
            props[fieldName] = FormState.selectAnInputProps(dispatch, state, fieldName);
        }
        return props;
    }

    interface SubmitPropsParameters<V extends AnyValues> extends InputPropsParameters {
        submit: SubmitHandler<V>;
        /**
         * Determine if `canSubmit` is false when there are any issues present.
         *
         * @default false
         */
        strict?: boolean;
    }

    export function selectSubmitProps<V extends AnyValues>(
        state: FormStateWithIssues<V>,
        {dispatch, submit, strict = false}: SubmitPropsParameters<V>
    ) {
        const {submitIssues, fields} = state;
        const hasSubmitIssues = FormFieldsState.getFields(state).some(
            name => submitIssues[name] && fields[name].wasModified < state.submitDoneAt
        );

        const {issues} = state;
        const hasIssues = FormFieldsState.getFields(state).some(name => issues[name]);

        const canSubmit = !hasIssues;

        const {isSubmitting, onSubmit} = FormSubmitState.selectSubmitProps(state, {
            submit: () => {
                if (canSubmit) {
                    return submit();
                }
                dispatch(FormAction.SubmitAttempt());
            },
            dispatch,
        });

        const isSubmitDisabled = (() => {
            if (strict) {
                return isSubmitting || hasIssues || hasSubmitIssues;
            } else {
                return isSubmitting;
            }
        })();

        return {
            canSubmit,
            isSubmitDisabled,
            isSubmitting,
            hasSubmitIssues,
            hasIssues,
            onSubmit,
        };
    }

    export type Action<V extends AnyValues> = ActionNarrow<V> | ActionLike;
    export type ActionNarrow<V extends AnyValues> =
        | FormFieldsState.Action<V>
        | FormSubmitState.Action<V>
        | FormAction.GeneralAction<V>;

    export function reduce<V extends AnyValues>(formState: FormState<V>, action: Action<V>): FormState<V> {
        if (FormAction.Update.match(action)) {
            return produce(formState, action.recipe);
        }
        const fieldsState = FormFieldsState.reduce(formState, action);
        if (fieldsState !== formState) {
            formState = {...formState, ...fieldsState};
        }
        const submitState = FormSubmitState.reduce(formState, action);
        if (submitState !== formState) {
            formState = {...formState, ...submitState};
        }
        return formState;
    }

    export function submitGotDefect() {
        return SubmitGotDefect;
    }

    export function submitGotResult() {
        return SubmitGotResult;
    }
}

// ========================================
// General FormState actions

export namespace FormAction {
    type UpdateRecipe<V extends AnyValues> = (current: FormState<V>) => void | FormState<V>;

    export type Update<V extends AnyValues> = ReturnType<typeof Update<V>>;

    export function Update<V extends AnyValues>(recipe: UpdateRecipe<V>) {
        return {
            type: Update.type,
            recipe,
        };
    }

    Update.type = 'FormState:updateState' as const;
    Update.match = createMatcherFor(Update);

    export type Reset = ReturnType<typeof Reset>;

    export function Reset() {
        return {
            type: Reset.type,
        };
    }

    Reset.type = 'FormState:resetState' as const;
    Reset.match = createMatcherFor(Reset);

    export type GeneralAction<V extends AnyValues> = Update<V> | Reset;

    export function bindGeneralActions<V extends AnyValues>(dispatch: DispatchLike<GeneralAction<V>>) {
        return {
            update: (recipe: UpdateRecipe<V>) => dispatch(Update(recipe)),
            reset: () => dispatch(Reset()),
        };
    }

    export function bindAllActions<V extends AnyValues>(dispatch: DispatchLike<FormState.Action<V>>) {
        return {
            ...FormAction.bindFieldsActions<V>(dispatch),
            ...FormAction.bindSubmitActions<V>(dispatch),
            ...bindGeneralActions<V>(dispatch),
        };
    }
}

// ================================================================================
// # Utilities
// ================================================================================

type Falsy = false | undefined | null | 0 | '';
type OmitType<T extends ActionLike> = Omit<T, 'type'>;

type EventLike = {
    preventDefault: () => void;
};

function createMatcherFor<A extends ActionLike>(actionCreator: {
    (...args: any[]): A;
    type: string;
}): (action: ActionLike) => action is A {
    return (action): action is A => action.type === actionCreator.type;
}

export type ValueOrEvent<T> = T | {currentTarget: {value: T}};
export namespace ValueOrEvent {
    export function isEvent<T>(value: ValueOrEvent<T>) {
        return !!value && typeof value === 'object' && 'currentTarget' in value;
    }
}

type MaybePromise<T> = T | Promise<T>;
namespace MaybePromise {
    export async function classify<V>(
        value: MaybePromise<V>
    ): Promise<{isImmediate: true; value: V} | {isImmediate: false; value: Promise<V>}> {
        if (!(value instanceof Promise)) {
            return {isImmediate: true, value};
        }
        return new Promise(resolve => {
            // Promise resolves only ones, so it's okay to call resolve multiple times.
            // The only case when setTimeout will not execute first, is if value is already resolved.
            window.setTimeout(() => {
                resolve({isImmediate: false, value});
            });
            value.then(value => {
                resolve({isImmediate: true, value});
            });
        });
    }
}
